import React, { useMemo } from "react";
import { Flex, utils } from "@thenounproject/lingo-core";
import { Inspectable } from "@constants/Inspector";
import { DisplayProperties } from "@redux/actions/items/useBatchSaveItems";
import { DisplayPanelButton } from "./DisplayPanelButton";
import { useTheme } from "styled-components";
import { ActionContext } from "../registry/types";
import { useSelectionActions } from "@src/contexts/useSelectionHooks";
import { assetHasFile } from "../registry/actionHelpers";

type Props = {
  context: ActionContext;
  onUpdateDisplayProps: (displayProperties: DisplayProperties, items?: Inspectable[]) => void;
  onClose?: () => void;
};

const { getColor, getBorder } = utils;

// DisplayPanel buttons for toggling metadata, caption, downloads, and autoplay options
export const MetaOptions: React.FC<Props> = ({ context, onUpdateDisplayProps, onClose }) => {
  const {
    selection: { assetItems, motionAssets },
    location: { inAssetDetail },
  } = context;
  const theme = useTheme();

  const { getEditHandler } = useSelectionActions();
  const editHandler = getEditHandler("kitCanvas");

  const { showMetadata, hasCaption, allowDownload, autoplay } = useMemo(() => {
    const getDisplayProperties = (i: Inspectable) => i.item?.displayProperties;
    return {
      showMetadata: assetItems.some(i => getDisplayProperties(i)?.showMetadata === true),
      hasCaption: assetItems.some(i => (getDisplayProperties(i)?.caption?.length ?? 0) > 0),
      allowDownload: assetItems.some(i => getDisplayProperties(i)?.allowDownload === true),
      autoplay: assetItems.some(i => getDisplayProperties(i)?.autoplay === true),
    };
  }, [assetItems]);

  const selectionContainsAssetItems = Boolean(assetItems.length);
  const selectionContainsMotionAssets = Boolean(motionAssets.length);

  const isDownloadable = assetItems.some(assetHasFile);
  const primaryColor = getColor(theme?.primaryColor, "primary");
  const primaryColorDark = getColor(theme?.primaryColorDark, "primaryDark");

  const defaultBorderStyle = { border: getBorder("default") };

  if (inAssetDetail && !isDownloadable && !selectionContainsMotionAssets) return null;

  const getToggleThemeOverrides = (active: boolean) => ({
    primaryColor: active ? primaryColor : "white",
    primaryColorDark: active ? primaryColorDark : "grayLight",
    primaryColorTint: active ? getColor(theme.primaryColorTint, "white") : "black",
  });

  const handleCaptionToggle = () => {
    const singleAssetItem = assetItems.length === 1 ? assetItems[0] : null;
    const isAddingCaption = !hasCaption;

    onUpdateDisplayProps({
      caption: hasCaption ? "" : "## Add a caption\n\nDouble click to edit",
    });

    if (isAddingCaption && editHandler && singleAssetItem) {
      editHandler(singleAssetItem.item.uuid);
      onClose?.();
    }
  };

  return (
    <Flex key="meta-options" gap="6px">
      {!inAssetDetail && selectionContainsAssetItems && (
        <DisplayPanelButton
          icon="info.file-meta"
          active={showMetadata}
          flex={1}
          style={defaultBorderStyle}
          themeOverrides={getToggleThemeOverrides(showMetadata)}
          label="Meta"
          onClick={() => {
            onUpdateDisplayProps({
              show_metadata: !showMetadata,
            });
          }}
        />
      )}
      {isDownloadable && (
        <DisplayPanelButton
          icon="info.cloud-download"
          active={allowDownload}
          flex={1}
          style={defaultBorderStyle}
          themeOverrides={getToggleThemeOverrides(allowDownload)}
          label="Download"
          onClick={() => {
            onUpdateDisplayProps({
              allow_download: !allowDownload,
            });
          }}
        />
      )}
      {!inAssetDetail && selectionContainsAssetItems && (
        <DisplayPanelButton
          icon="content.note"
          active={hasCaption}
          flex={1}
          style={defaultBorderStyle}
          themeOverrides={getToggleThemeOverrides(hasCaption)}
          label="Caption"
          onClick={handleCaptionToggle}
        />
      )}
      {selectionContainsMotionAssets && (
        <DisplayPanelButton
          icon="action.play"
          active={autoplay}
          flex={1}
          style={defaultBorderStyle}
          label="Autoplay"
          themeOverrides={getToggleThemeOverrides(autoplay)}
          onClick={() => {
            onUpdateDisplayProps(
              {
                autoplay: !autoplay,
              },
              motionAssets
            );
          }}
        />
      )}
    </Flex>
  );
};
